import React from 'react';
import './LogSheetRenderer.css';

const LogSheetRenderer = ({ logSheet, userProfile, tripMeta, pickupLocation, dropoffLocation }) => {
    if (!logSheet) return null;
    
    // Use passed locations if available, else fallback
    const displayFrom = pickupLocation || logSheet.start_location_str || "See Remarks";
    const displayTo = dropoffLocation || logSheet.end_location_str || "See Remarks";

    const { log_date, duty_status_events, hos_recap, total_miles_today } = logSheet;
    
    // Metadata fallback
    const displayDriver = userProfile?.driverName || "__________________";
    const displayCarrier = userProfile?.carrierName || "SpotLogs Transport LLC";
    const displayOffice = userProfile?.officeAddress || "123 Main St, Chicago, IL";
    const displayTerminal = userProfile?.terminalAddress || "456 Terminal Rd, Gary, IN";
    const displayVehicle = userProfile?.vehicleUnit || "TRUCK-01";
    const displayTrailer = tripMeta?.trailerNumber || "TRAILER-99";
    const displayShipping = tripMeta?.shippingDocs || "MANIFEST #882103";
    const displayCommodity = tripMeta?.shipperCommodity || "Steel Coils / Acme Corp";

    // Constants for Grid Dimensions
    const WIDTH = 800;
    const HEIGHT = 200;
    const MARGIN = { top: 30, right: 30, bottom: 20, left: 80 };
    const GRID_WIDTH = WIDTH - MARGIN.left - MARGIN.right;
    const ROW_HEIGHT = 35;
    const ROWS = ['1. OFF', '2. SB', '3. D', '4. ON'];

    // Helper to get X position
    const getX = (timeStr) => {
        const [h, m] = timeStr.split(':').map(Number);
        const totalMinutes = h * 60 + m;
        return MARGIN.left + (totalMinutes / 1440) * GRID_WIDTH;
    };

    // Helper to get Y position
    const getY = (status) => {
        let rowIdx = 0;
        if (status === 'OFF_DUTY') rowIdx = 0;
        if (status === 'SLEEPER_BERTH') rowIdx = 1;
        if (status === 'DRIVING') rowIdx = 2;
        if (status === 'ON_DUTY_NOT_DRIVING') rowIdx = 3;
        return MARGIN.top + (rowIdx * ROW_HEIGHT) + (ROW_HEIGHT / 2);
    };

    let pathD = "";
    let lastY = null;
    const sortedEvents = [...duty_status_events].sort((a,b) => a.start_time.localeCompare(b.start_time));

    sortedEvents.forEach((event, index) => {
        const startX = getX(event.start_time);
        const endX = getX(event.end_time);
        const y = getY(event.status);

        if (index === 0) {
            pathD += `M ${startX} ${y} `;
        } else {
            pathD += `L ${startX} ${lastY} `; 
            pathD += `L ${startX} ${y} `; 
        }
        pathD += `L ${endX} ${y} `;
        lastY = y;
    });

    // Date Format
    const dateObj = new Date(log_date);
    const month = dateObj.getMonth() + 1;
    const day = dateObj.getDate() + 1;
    const year = dateObj.getFullYear();

    return (
        <div className="log-sheet-paper">
            {/* Top Header */}
            <div className="log-paper-header">
                <div className="title-section">
                    <h1>Driver's Daily Log</h1>
                    <p style={{fontSize: '9px', margin: '2px 0'}}>(24 hours)</p>
                </div>
                <div className="date-fields">
                    <div>
                        <div className="date-box">{month}</div>
                        <span className="date-label">(month)</span>
                    </div>
                    <div>
                        <div className="date-box">{day}</div>
                        <span className="date-label">(day)</span>
                    </div>
                    <div>
                        <div className="date-box">{year}</div>
                        <span className="date-label">(year)</span>
                    </div>
                </div>
            </div>

            <div className="log-top-grid">
                <div className="from-to-section">
                    <div className="line-field">
                        <label>From:</label>
                        <div className="value">{displayFrom}</div>
                    </div>
                    <div className="line-field">
                        <label>To:</label>
                        <div className="value">{displayTo}</div>
                    </div>
                    <div className="box-fields">
                        <div style={{flex: 1}}>
                            <div className="square-box">{Math.round(total_miles_today)}</div>
                            <span className="box-label">Total Miles Driving Today</span>
                        </div>
                        <div style={{flex: 1}}>
                            <div className="square-box">{Math.round(total_miles_today + 50)}</div>
                            <span className="box-label">Total Mileage Today</span>
                        </div>
                    </div>
                    <div className="line-field" style={{marginTop: '10px'}}>
                         <div className="value" style={{fontSize: '11px', fontWeight: 'bold'}}>
                            {displayVehicle} / {displayTrailer}
                         </div>
                    </div>
                    <span className="box-label" style={{textAlign: 'left'}}>Truck/Tractor and Trailer Numbers or License Plate(s)/State</span>
                </div>

                <div className="carrier-info">
                    <div className="line-field">
                        <label>Carrier:</label>
                        <div className="value">{displayCarrier}</div>
                    </div>
                    <div className="line-field">
                        <label>Main Office:</label>
                        <div className="value">{displayOffice}</div>
                    </div>
                    <div className="line-field">
                        <label>Home Terminal:</label>
                        <div className="value">{displayTerminal}</div>
                    </div>
                </div>
            </div>

            {/* Grid SVG */}
            <div className="log-svg-container">
                <svg viewBox={`0 0 ${WIDTH} ${HEIGHT}`} className="log-svg">
                    {/* Background rectangle */}
                    <rect x={MARGIN.left} y={MARGIN.top} width={GRID_WIDTH} height={4 * ROW_HEIGHT} fill="#fff" stroke="#000" strokeWidth="1" />
                    
                    {/* Hour Vertical Lines */}
                    {Array.from({ length: 25 }).map((_, i) => {
                        const x = MARGIN.left + (i / 24) * GRID_WIDTH;
                        const isNoon = i === 12;
                        const isMid = i === 0 || i === 24;
                        return (
                            <g key={i}>
                                <line 
                                    x1={x} y1={MARGIN.top} 
                                    x2={x} y2={MARGIN.top + (4 * ROW_HEIGHT)} 
                                    stroke={isMid || isNoon ? "#000" : "#999"} 
                                    strokeWidth={isMid || isNoon ? "1.5" : "0.5"}
                                />
                                <text x={x} y={MARGIN.top - 8} fontSize="9" textAnchor="middle" fontWeight="bold">
                                    {isMid ? 'Mid' : isNoon ? 'Noon' : i > 12 ? i - 12 : i}
                                </text>
                                {/* 15-min dashes */}
                                {i < 24 && [1, 2, 3].map(q => {
                                    const qx = x + (q / 96) * GRID_WIDTH;
                                    return <line key={q} x1={qx} y1={MARGIN.top} x2={qx} y2={MARGIN.top + (4 * ROW_HEIGHT)} stroke="#ddd" strokeWidth="0.5" strokeDasharray="2,2" />
                                })}
                            </g>
                        );
                    })}

                    {/* Duty Status Horizontal Lines */}
                    {ROWS.map((label, i) => (
                        <g key={label}>
                            <text x={5} y={MARGIN.top + (i * ROW_HEIGHT) + (ROW_HEIGHT/2) + 4} fontSize="9" fontWeight="bold">{label}</text>
                            <line 
                                x1={MARGIN.left} y1={MARGIN.top + (i * ROW_HEIGHT)} 
                                x2={WIDTH - MARGIN.right} y2={MARGIN.top + (i * ROW_HEIGHT)} 
                                stroke="#000" strokeWidth="0.5" 
                            />
                        </g>
                    ))}

                    <path d={pathD} stroke="#000" strokeWidth="2.5" fill="none" strokeLinejoin="bevel" />
                </svg>
            </div>

            {/* Remarks Section */}
            <div className="log-remarks-container">
                <div className="remarks-meta">
                    <div className="meta-field">
                        <label>Shipping Documents:</label>
                        <div className="value">{displayShipping}</div>
                    </div>
                    <div className="meta-field">
                        <label>Shipper & Commodity:</label>
                        <div className="value">{displayCommodity}</div>
                    </div>
                </div>
                <div className="remarks-content">
                    <h3>Remarks</h3>
                    <table className="remarks-table">
                        <tbody>
                            {sortedEvents.filter(e => e.remark).map((e, i) => (
                                <tr key={i}>
                                    <td width="60"><strong>{e.start_time}</strong></td>
                                    <td>{e.remark}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Bottom Recap */}
            <div className="log-recap-footer">
                <div className="recap-column">
                    <div className="recap-header">Daily Totals</div>
                    <div className="recap-row"><span>On-Duty Today:</span> <span>{Math.round(hos_recap?.total_today || 0)}h</span></div>
                    <div className="recap-row"><span>Driving Today:</span> <span>{Math.round(total_miles_today / 60)}h</span></div>
                </div>
                <div className="recap-column">
                    <div className="recap-header">70 Hour / 8 Day cycle</div>
                    <div className="recap-row"><span>A. Total last 7 days:</span> <span>{Math.round(hos_recap?.cycle_total || 0)}h</span></div>
                    <div className="recap-row"><span>B. Available tomorrow:</span> <span>{Math.round(hos_recap?.available_tomorrow || 0)}h</span></div>
                </div>
                <div className="recap-column">
                    <div className="recap-header">Compliance Check</div>
                    <p style={{fontStyle: 'italic'}}>If 34 consecutive hours were off duty, weekly hours have been reset.</p>
                </div>
            </div>

            <div className="log-signature-area">
                <div className="signature-line">
                    {displayDriver}
                </div>
            </div>
            <p style={{fontSize: '9px', textAlign: 'right', marginTop: '4px'}}>Driver's Signature (I certify these entries are true and correct)</p>
        </div>
    );
};

export default LogSheetRenderer;
